import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { FaPlus, FaEdit, FaTrash, FaFileAlt, FaChevronLeft, FaChevronRight, FaWallet, FaListAlt, FaTags, FaCreditCard } from 'react-icons/fa';
import PageLayout from './PageLayout';
import { theme, t } from '../theme';
import { useConfirm } from './ConfirmProvider';
import financasApi from '../services/financasApi';
import NovaTransacaoModal from './NovaTransacaoModal';
import ExtratoMensalModal from './ExtratoMensalModal';

const fmtMoeda = (v) => `R$ ${(Number(v) || 0).toFixed(2).replace('.', ',')}`;

const TABS = [
  { id: 'transacoes', label: 'Transações', icon: <FaListAlt /> },
  { id: 'caixa', label: 'Caixa', icon: <FaWallet /> },
  { id: 'categorias', label: 'Categorias', icon: <FaTags /> },
  { id: 'metodos', label: 'Métodos', icon: <FaCreditCard /> },
];

export default function Financas() {
  const [searchParams, setSearchParams] = useSearchParams();
  const tabAtiva = searchParams.get('tab') || 'transacoes';
  const setTab = (id) => setSearchParams({ tab: id });

  return (
    <PageLayout title="Finanças" subtitle="Gestão de receitas, despesas, lucro e fluxo de caixa">
      {/* Tabs */}
      <div style={{ display: 'flex', gap: 4, borderBottom: `1px solid ${theme.colors.border}`, marginBottom: 18, overflowX: 'auto' }}>
        {TABS.map(tab => (
          <button key={tab.id} onClick={() => setTab(tab.id)}
            style={{
              padding: '10px 16px', border: 'none', cursor: 'pointer',
              background: 'transparent', fontSize: 14, fontWeight: 600,
              color: tabAtiva === tab.id ? theme.colors.primary : theme.colors.textMuted,
              borderBottom: `2px solid ${tabAtiva === tab.id ? theme.colors.primary : 'transparent'}`,
              display: 'flex', alignItems: 'center', gap: 6, whiteSpace: 'nowrap',
            }}>
            {tab.icon} {tab.label}
          </button>
        ))}
      </div>

      {tabAtiva === 'transacoes' && <TabTransacoes />}
      {tabAtiva === 'caixa' && <TabCaixa />}
      {tabAtiva === 'categorias' && <TabCategorias />}
      {tabAtiva === 'metodos' && <TabMetodos />}
    </PageLayout>
  );
}

// ============================================================================
// TAB: Transações
// ============================================================================

function TabTransacoes() {
  const confirm = useConfirm();
  const [periodo, setPeriodo] = useState('mes');
  const [resumo, setResumo] = useState({ receitas: 0, despesas: 0, lucro: 0, count_receitas: 0, count_despesas: 0 });
  const [transacoes, setTransacoes] = useState([]);
  const [filtroTipo, setFiltroTipo] = useState('todas');
  const [showNova, setShowNova] = useState(false);
  const [editando, setEditando] = useState(null);
  const [showExtrato, setShowExtrato] = useState(false);
  const [caixaInicial, setCaixaInicial] = useState({ valor: 0, escopo: 'mes', referencia: '' });
  const [caixaEscopo, setCaixaEscopo] = useState('mes');
  const [caixaInputValor, setCaixaInputValor] = useState('');

  async function carregar() {
    try {
      const r = await financasApi.resumo({ periodo });
      setResumo(r.data);
      const t = await financasApi.listarTransacoes({ periodo });
      setTransacoes(t.data || []);
    } catch {}
  }

  async function carregarCaixaInicial() {
    try {
      const hoje = new Date().toISOString().split('T')[0];
      const ref = caixaEscopo === 'mes' ? `${hoje.slice(0, 7)}-01` : hoje;
      const r = await financasApi.getCaixaInicial({ escopo: caixaEscopo, ref });
      setCaixaInicial(r.data);
      setCaixaInputValor(Number(r.data.valor || 0).toFixed(2).replace('.', ','));
    } catch {}
  }

  useEffect(() => { carregar(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [periodo]);
  useEffect(() => { carregarCaixaInicial(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [caixaEscopo]);

  async function salvarCaixaInicial() {
    const valor = parseFloat(String(caixaInputValor).replace(/\./g, '').replace(',', '.')) || 0;
    const hoje = new Date().toISOString().split('T')[0];
    const ref = caixaEscopo === 'mes' ? `${hoje.slice(0, 7)}-01` : hoje;
    try {
      await financasApi.setCaixaInicial({ escopo: caixaEscopo, referencia: ref, valor });
      carregarCaixaInicial();
    } catch {}
  }

  async function deletar(id) {
    if (!await confirm('Excluir esta transação?', { danger: true, confirmText: 'Excluir' })) return;
    try { await financasApi.deletarTransacao(id); carregar(); } catch (e) {
      alert(e.response?.data?.erro || 'Erro ao excluir');
    }
  }

  const transacoesFiltradas = filtroTipo === 'todas' ? transacoes : transacoes.filter(t => t.tipo === filtroTipo);

  // Agrupa por categoria
  const grupos = {};
  transacoesFiltradas.forEach(tx => {
    const k = tx.categoria_nome || '(sem categoria)';
    if (!grupos[k]) grupos[k] = [];
    grupos[k].push(tx);
  });

  return (
    <>
      {/* Caixa inicial */}
      <div style={{ background: '#fff', border: `1px solid ${theme.colors.border}`, borderRadius: 12, padding: 14, marginBottom: 16 }}>
        <div className="finance-caixa-inicial" style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <strong style={{ fontSize: 14 }}>💼 Caixa Inicial</strong>
          <div style={{ display: 'inline-flex', borderRadius: 8, overflow: 'hidden', border: `1px solid ${theme.colors.border}` }}>
            {[{ v: 'dia', l: 'Do dia' }, { v: 'mes', l: 'Do mês' }].map(o => (
              <button key={o.v} onClick={() => setCaixaEscopo(o.v)}
                style={{
                  padding: '5px 12px', border: 'none', cursor: 'pointer', fontSize: 12, fontWeight: 600,
                  background: caixaEscopo === o.v ? theme.colors.primary : '#fff',
                  color: caixaEscopo === o.v ? '#fff' : theme.colors.textMuted,
                }}>{o.l}</button>
            ))}
          </div>
          <input value={caixaInputValor} onChange={e => {
            const d = String(e.target.value).replace(/\D/g, '');
            setCaixaInputValor(d ? (parseInt(d, 10) / 100).toFixed(2).replace('.', ',') : '');
          }} placeholder="0,00" style={{ ...t.input, width: 110 }} />
          <button onClick={salvarCaixaInicial} style={{ ...t.btnSecondary, padding: '6px 14px', fontSize: 13 }}>Salvar</button>
        </div>
        <small style={{ display: 'block', marginTop: 8, fontSize: 12, color: theme.colors.textMuted }}>Não conta como entrada nem lucro.</small>
      </div>

      {/* Filtro de período */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center' }}>
        <select value={periodo} onChange={e => setPeriodo(e.target.value)} style={{ ...t.select, width: 200 }}>
          <option value="hoje">Hoje</option>
          <option value="mes">Este mês</option>
          <option value="tudo">Tudo</option>
        </select>
        <div style={{ flex: 1 }}></div>
        <button onClick={() => setShowExtrato(true)} style={t.btnSecondary}><FaFileAlt /> Extrato Mensal</button>
        <button onClick={() => { setEditando(null); setShowNova(true); }} style={t.btnPrimary}><FaPlus /> Nova Transação</button>
      </div>

      {/* Cards resumo */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12, marginBottom: 18 }}>
        {[
          { l: 'Receitas', v: resumo.receitas, cnt: resumo.count_receitas, cor: '#16a34a', bg: '#f0fdf4' },
          { l: 'Despesas', v: resumo.despesas, cnt: resumo.count_despesas, cor: '#dc2626', bg: '#fef2f2' },
          { l: 'Lucro', v: resumo.lucro, cor: resumo.lucro >= 0 ? '#16a34a' : '#dc2626', bg: '#f9fafb' },
        ].map((c, i) => (
          <div key={i} style={{ background: c.bg, borderRadius: 12, padding: 16, border: `1px solid ${c.cor}22` }}>
            <div style={{ fontSize: 11, color: theme.colors.textMuted, marginBottom: 6, fontWeight: 600, letterSpacing: 0.5, textTransform: 'uppercase' }}>{c.l}</div>
            <div style={{ fontSize: 22, fontWeight: 700, color: c.cor }}>{fmtMoeda(c.v)}</div>
            {c.cnt != null && <div style={{ fontSize: 11, color: theme.colors.textMuted, marginTop: 2 }}>{c.cnt} {c.cnt === 1 ? 'transação' : 'transações'}</div>}
          </div>
        ))}
      </div>

      {/* Sub-tabs */}
      <div style={{ display: 'flex', gap: 0, marginBottom: 12, borderRadius: 10, overflow: 'hidden', border: `1px solid ${theme.colors.border}`, maxWidth: 360 }}>
        {[{ v: 'todas', l: 'Todas' }, { v: 'receita', l: 'Receitas' }, { v: 'despesa', l: 'Despesas' }].map(o => (
          <button key={o.v} onClick={() => setFiltroTipo(o.v)}
            style={{
              flex: 1, padding: '8px 0', border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 600,
              background: filtroTipo === o.v ? theme.colors.bgSection : '#fff',
              color: filtroTipo === o.v ? theme.colors.primary : theme.colors.textMuted,
            }}>{o.l}</button>
        ))}
      </div>

      {/* Lista agrupada */}
      {Object.keys(grupos).length === 0 ? (
        <div style={{ textAlign: 'center', padding: 40, color: '#bbb' }}>Nenhuma transação no período.</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {Object.entries(grupos).map(([cat, txs]) => {
            const tot = txs.reduce((s, x) => s + (x.tipo === 'receita' ? Number(x.valor) : -Number(x.valor)), 0);
            return (
              <div key={cat} style={{ background: '#fff', border: `1px solid ${theme.colors.border}`, borderRadius: 12, overflow: 'hidden' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 14px', background: theme.colors.bgSection || '#F9FAFB', borderBottom: `1px solid ${theme.colors.border}` }}>
                  <strong style={{ fontSize: 13 }}>{cat}</strong>
                  <span style={{ fontSize: 13, fontWeight: 700, color: tot >= 0 ? '#16a34a' : '#dc2626' }}>{fmtMoeda(tot)}</span>
                </div>
                {txs.map(tx => (
                  <div key={tx.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', borderBottom: `1px solid ${theme.colors.borderLight || '#F3F4F6'}` }}>
                    <div style={{ width: 28, height: 28, borderRadius: '50%', background: tx.tipo === 'receita' ? '#16a34a15' : '#dc262615', color: tx.tipo === 'receita' ? '#16a34a' : '#dc2626', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14 }}>
                      {tx.tipo === 'receita' ? '↑' : '↓'}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 14, fontWeight: 500, color: theme.colors.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {tx.descricao || (tx.tipo === 'receita' ? (tx.cliente_nome || 'Receita') : (tx.fornecedor || 'Despesa'))}
                      </div>
                      <div style={{ fontSize: 11, color: theme.colors.textMuted }}>
                        {tx.data ? new Date(tx.data + 'T12:00:00').toLocaleDateString('pt-BR') : ''} · {tx.metodo_pagamento_nome}
                        {tx.pedido_id && <span style={{ marginLeft: 6, color: theme.colors.primary }}>· Pedido #{tx.pedido_id}</span>}
                      </div>
                    </div>
                    <div style={{ fontSize: 14, fontWeight: 700, color: tx.tipo === 'receita' ? '#16a34a' : '#dc2626' }}>
                      {tx.tipo === 'despesa' ? '−' : ''}{fmtMoeda(tx.valor)}
                    </div>
                    {!tx.pedido_pagamento_id && (
                      <>
                        <button onClick={() => { setEditando(tx); setShowNova(true); }} style={{ ...t.btnIcon, color: theme.colors.info }}><FaEdit /></button>
                        <button onClick={() => deletar(tx.id)} style={{ ...t.btnIcon, color: theme.colors.danger }}><FaTrash /></button>
                      </>
                    )}
                  </div>
                ))}
              </div>
            );
          })}
        </div>
      )}

      {showNova && <NovaTransacaoModal transacao={editando} onClose={() => { setShowNova(false); setEditando(null); }} onSaved={() => { setShowNova(false); setEditando(null); carregar(); }} />}
      {showExtrato && <ExtratoMensalModal onClose={() => setShowExtrato(false)} />}
    </>
  );
}

// ============================================================================
// TAB: Caixa (saldo por método, navegação por mês)
// ============================================================================

function TabCaixa() {
  const hoje = new Date();
  const [ref, setRef] = useState(`${hoje.getFullYear()}-${String(hoje.getMonth() + 1).padStart(2, '0')}`);
  const [dados, setDados] = useState(null);
  const [filtroTipo, setFiltroTipo] = useState('todas');

  useEffect(() => {
    financasApi.caixa({ ref }).then(r => setDados(r.data)).catch(() => {});
  }, [ref]);

  function moverMes(delta) {
    const [a, m] = ref.split('-').map(Number);
    const d = new Date(a, m - 1 + delta, 1);
    setRef(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`);
  }

  if (!dados) return <p style={{ textAlign: 'center', color: '#999', padding: 30 }}>Carregando...</p>;

  return (
    <>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 18 }}>
        <button onClick={() => moverMes(-1)} style={t.btnIcon}><FaChevronLeft /></button>
        <strong style={{ fontSize: 16 }}>
          {new Date(`${ref}-15`).toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' })}
        </strong>
        <button onClick={() => moverMes(1)} style={t.btnIcon}><FaChevronRight /></button>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12, marginBottom: 18 }}>
        {[
          { l: 'Saldo Total', v: dados.saldo_total, cor: dados.saldo_total >= 0 ? '#16a34a' : '#dc2626', bg: '#f9fafb' },
          { l: 'Entradas', v: dados.total_entradas, cor: '#16a34a', bg: '#f0fdf4' },
          { l: 'Saídas', v: dados.total_saidas, cor: '#dc2626', bg: '#fef2f2' },
        ].map((c, i) => (
          <div key={i} style={{ background: c.bg, borderRadius: 12, padding: 16, border: `1px solid ${c.cor}22` }}>
            <div style={{ fontSize: 11, color: theme.colors.textMuted, marginBottom: 6, fontWeight: 600, letterSpacing: 0.5, textTransform: 'uppercase' }}>{c.l}</div>
            <div style={{ fontSize: 22, fontWeight: 700, color: c.cor }}>{fmtMoeda(c.v)}</div>
          </div>
        ))}
      </div>

      {dados.caixa_inicial > 0 && (
        <div style={{ background: '#FEF9E7', border: '1px solid #FCD34D', borderRadius: 8, padding: 10, marginBottom: 14, fontSize: 13 }}>
          💼 Caixa inicial do mês: <strong>{fmtMoeda(dados.caixa_inicial)}</strong> (incluído no saldo total)
        </div>
      )}

      {/* Sub-tabs */}
      <div style={{ display: 'flex', gap: 0, marginBottom: 12, borderRadius: 10, overflow: 'hidden', border: `1px solid ${theme.colors.border}`, maxWidth: 360 }}>
        {[{ v: 'todas', l: 'Todas' }, { v: 'entradas', l: 'Entradas' }, { v: 'saidas', l: 'Saídas' }].map(o => (
          <button key={o.v} onClick={() => setFiltroTipo(o.v)}
            style={{
              flex: 1, padding: '8px 0', border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 600,
              background: filtroTipo === o.v ? theme.colors.bgSection : '#fff',
              color: filtroTipo === o.v ? theme.colors.primary : theme.colors.textMuted,
            }}>{o.l}</button>
        ))}
      </div>

      {/* Saldo por método */}
      {dados.por_metodo.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 40, color: '#bbb' }}>Sem movimentações no mês.</div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 12 }}>
          {dados.por_metodo.map(m => {
            const ult = filtroTipo === 'entradas' ? m.ultimas.filter(x => x.tipo === 'receita')
                       : filtroTipo === 'saidas' ? m.ultimas.filter(x => x.tipo === 'despesa') : m.ultimas;
            return (
              <div key={m.metodo_id} style={{ background: '#fff', border: `1px solid ${theme.colors.border}`, borderRadius: 12, padding: 14 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 10 }}>
                  <strong style={{ fontSize: 15 }}>{m.metodo}</strong>
                  <span style={{ fontSize: 11, color: theme.colors.textMuted }}>{m.count} {m.count === 1 ? 'mov' : 'movs'}</span>
                </div>
                <div style={{ fontSize: 22, fontWeight: 700, color: m.saldo >= 0 ? '#16a34a' : '#dc2626', marginBottom: 8 }}>
                  {fmtMoeda(m.saldo)}
                </div>
                <div style={{ display: 'flex', gap: 14, fontSize: 12, color: theme.colors.textMuted, marginBottom: 10 }}>
                  <span>↑ <span style={{ color: '#16a34a' }}>{fmtMoeda(m.entradas)}</span></span>
                  <span>↓ <span style={{ color: '#dc2626' }}>{fmtMoeda(m.saidas)}</span></span>
                </div>
                {ult.length > 0 && (
                  <div style={{ borderTop: `1px solid ${theme.colors.borderLight || '#F3F4F6'}`, paddingTop: 8 }}>
                    {ult.slice(0, 4).map(tx => (
                      <div key={tx.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 12, padding: '4px 0', color: theme.colors.textMuted }}>
                        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '60%' }}>
                          {tx.descricao || tx.cliente_nome || tx.fornecedor || 'Transação'}
                        </span>
                        <span style={{ fontWeight: 600, color: tx.tipo === 'receita' ? '#16a34a' : '#dc2626' }}>
                          {tx.tipo === 'despesa' ? '−' : ''}{fmtMoeda(tx.valor)}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </>
  );
}

// ============================================================================
// TAB: Categorias
// ============================================================================

function TabCategorias() {
  const confirm = useConfirm();
  const [tipo, setTipo] = useState('receita');
  const [categorias, setCategorias] = useState([]);
  const [novoNome, setNovoNome] = useState('');

  async function carregar() {
    try {
      const res = await financasApi.listarCategorias({ tipo });
      setCategorias(res.data || []);
    } catch {}
  }
  useEffect(() => { carregar(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [tipo]);

  async function criar(e) {
    e.preventDefault();
    if (!novoNome.trim()) return;
    try {
      await financasApi.criarCategoria({ nome: novoNome.trim(), tipo });
      setNovoNome(''); carregar();
    } catch (e2) { alert(e2.response?.data?.erro || 'Erro'); }
  }

  async function toggleAtivo(c) {
    try { await financasApi.atualizarCategoria(c.id, { ativo: !c.ativo }); carregar(); } catch {}
  }

  async function excluir(c) {
    if (!await confirm(`Excluir "${c.nome}"?`, { danger: true, confirmText: 'Excluir' })) return;
    try { await financasApi.deletarCategoria(c.id); carregar(); } catch (e) {
      alert(e.response?.data?.erro || 'Erro ao excluir');
    }
  }

  return (
    <>
      <div style={{ display: 'inline-flex', borderRadius: 10, overflow: 'hidden', border: `1px solid ${theme.colors.border}`, marginBottom: 16 }}>
        {[{ v: 'receita', l: 'Receita' }, { v: 'despesa', l: 'Despesa' }].map(o => (
          <button key={o.v} onClick={() => setTipo(o.v)}
            style={{
              padding: '8px 18px', border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 600,
              background: tipo === o.v ? theme.colors.primary : '#fff',
              color: tipo === o.v ? '#fff' : theme.colors.textMuted,
            }}>{o.l}</button>
        ))}
      </div>

      <form onSubmit={criar} style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
        <input value={novoNome} onChange={e => setNovoNome(e.target.value)}
          placeholder={`Nova categoria de ${tipo}`} style={{ ...t.input, flex: 1, maxWidth: 320 }} />
        <button type="submit" style={t.btnPrimary}><FaPlus /> Adicionar</button>
      </form>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {categorias.map(c => (
          <div key={c.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', background: '#fff', border: `1px solid ${theme.colors.border}`, borderRadius: 10, opacity: c.ativo ? 1 : 0.5 }}>
            <span style={{ flex: 1, fontWeight: 500 }}>{c.nome}</span>
            {c.is_sistema && <span style={{ fontSize: 10, padding: '2px 8px', background: theme.colors.bgSection, borderRadius: 10, color: theme.colors.textMuted }}>padrão</span>}
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: theme.colors.textMuted, cursor: 'pointer' }}>
              <input type="checkbox" checked={c.ativo} onChange={() => toggleAtivo(c)} />
              {c.ativo ? 'Ativa' : 'Inativa'}
            </label>
            {!c.is_sistema && (
              <button onClick={() => excluir(c)} style={{ ...t.btnIcon, color: theme.colors.danger }}><FaTrash /></button>
            )}
          </div>
        ))}
      </div>
    </>
  );
}

// ============================================================================
// TAB: Métodos de Pagamento
// ============================================================================

function TabMetodos() {
  const confirm = useConfirm();
  const [metodos, setMetodos] = useState([]);
  const [novoNome, setNovoNome] = useState('');

  async function carregar() {
    try {
      const res = await financasApi.listarMetodos();
      setMetodos(res.data || []);
    } catch {}
  }
  useEffect(() => { carregar(); }, []);

  async function criar(e) {
    e.preventDefault();
    if (!novoNome.trim()) return;
    try {
      await financasApi.criarMetodo({ nome: novoNome.trim() });
      setNovoNome(''); carregar();
    } catch (e2) { alert(e2.response?.data?.erro || 'Erro'); }
  }

  async function toggleAtivo(m) {
    try { await financasApi.atualizarMetodo(m.id, { ativo: !m.ativo }); carregar(); } catch {}
  }

  async function excluir(m) {
    if (!await confirm(`Excluir "${m.nome}"?`, { danger: true, confirmText: 'Excluir' })) return;
    try { await financasApi.deletarMetodo(m.id); carregar(); } catch (e) {
      alert(e.response?.data?.erro || 'Erro ao excluir');
    }
  }

  return (
    <>
      <form onSubmit={criar} style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
        <input value={novoNome} onChange={e => setNovoNome(e.target.value)}
          placeholder="Novo método de pagamento" style={{ ...t.input, flex: 1, maxWidth: 320 }} />
        <button type="submit" style={t.btnPrimary}><FaPlus /> Adicionar</button>
      </form>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {metodos.map(m => (
          <div key={m.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', background: '#fff', border: `1px solid ${theme.colors.border}`, borderRadius: 10, opacity: m.ativo ? 1 : 0.5 }}>
            <span style={{ flex: 1, fontWeight: 500 }}>{m.nome}</span>
            {m.is_sistema && <span style={{ fontSize: 10, padding: '2px 8px', background: theme.colors.bgSection, borderRadius: 10, color: theme.colors.textMuted }}>padrão</span>}
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: theme.colors.textMuted, cursor: 'pointer' }}>
              <input type="checkbox" checked={m.ativo} onChange={() => toggleAtivo(m)} />
              {m.ativo ? 'Ativo' : 'Inativo'}
            </label>
            {!m.is_sistema && (
              <button onClick={() => excluir(m)} style={{ ...t.btnIcon, color: theme.colors.danger }}><FaTrash /></button>
            )}
          </div>
        ))}
      </div>
    </>
  );
}
